/* ═════════════════════════════════════════════════
   Skannaus — «Skannaa kameralla» tarran numeron syöttöön (päätös 54, Toni 2.10.2026 «kyllä»).

   Kaksi lukijaa, yksi tulos:
     · Android Chrome: selaimen oma BarcodeDetector (qr_code) — ei kirjastoa.
     · iOS Safari ja muut: jsQR (Apache-2.0) PAIKALLISENA tiedostona /jsqr.js
       (web/public, ei CDN:stä — kenttä on verkon takana jota emme hallitse,
       ja kolmannen osapuolen skripti sovelluksessa on oma riskinsä).
   Kirjasto ladataan vasta kun kameraa pyydetään: Android ei tarvitse sitä.

   Vaatii https:n ja kameraluvan. Jos lupa evätään tai kameraa ei ole, numeron
   voi edelleen kirjoittaa käsin — tämä komponentti ei korvaa syöttöä vaan
   istuu sen viereen. Skannattu QR avaa SAMAN näkymän kuin numero
   (#/k/<n>), ja kortti aukeaa vain kirjautuneelle (päätös 35).

   Luettu QR on osoite sampo.pro/k/<koodi>/<n> tai <instanssi>/k/<n>; myös
   pelkkä numero hyväksytään (painettu tarra ilman osoitetta).
   ═════════════════════════════════════════════════ */
import { useEffect, useRef, useState } from "react";
import { t } from "./kieli";

type Tila = "suljettu" | "avataan" | "lukee" | "ei_tukea" | "ei_lupaa" | "ei_kameraa" | "virhe";

declare global {
  interface Window { jsQR?: (data: Uint8ClampedArray, w: number, h: number) => { data: string } | null }
  var BarcodeDetector: any;
}

/** Tarran numero luetusta tekstistä, tai null. Hyväksyy /k/<koodi>/<n>, /k/<n> ja pelkän numeron. */
export function tarranNumero(teksti: string): number | null {
  const s = teksti.trim();
  const m = /\/k\/(?:[a-z0-9_-]+\/)?([1-9][0-9]{0,8})(?:[/?#]|$)/i.exec(s) ?? /^#?\/?k\/([1-9][0-9]{0,8})$/i.exec(s);
  if (m) return Number(m[1]);
  if (/^[1-9][0-9]{0,8}$/.test(s)) return Number(s);
  return null;
}

function lataaJsQR(): Promise<NonNullable<Window["jsQR"]>> {
  if (window.jsQR) return Promise.resolve(window.jsQR);
  return new Promise((ok, ei) => {
    const s = document.createElement("script");
    s.src = "/jsqr.js"; s.async = true;
    s.onload = () => (window.jsQR ? ok(window.jsQR) : ei(new Error("jsqr.js latautui mutta jsQR puuttuu")));
    s.onerror = () => ei(new Error("jsqr.js ei latautunut"));
    document.head.appendChild(s);
  });
}

export function Skannaus({ loytyi }: { loytyi: (numero: number, raaka: string) => void }) {
  const [tila, setTila] = useState<Tila>("suljettu");
  const [viesti, setViesti] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const virta = useRef<MediaStream | null>(null);
  const kehys = useRef<number>(0);

  const sulje = () => {
    cancelAnimationFrame(kehys.current);
    virta.current?.getTracks().forEach((t) => t.stop());
    virta.current = null;
    setTila("suljettu");
  };
  useEffect(() => () => { cancelAnimationFrame(kehys.current); virta.current?.getTracks().forEach((t) => t.stop()); }, []);

  const tuettu = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const turvallinen = typeof window !== "undefined" && (window.isSecureContext || location.hostname === "localhost");

  const avaa = async () => {
    setViesti(null);
    if (!turvallinen) { setTila("ei_tukea"); setViesti(t("ska.vaatii_https")); return; }
    if (!tuettu) { setTila("ei_tukea"); setViesti(t("ska.ei_tukea")); return; }
    setTila("avataan");
    let lukija: "barcodedetector" | "jsqr";
    let detector: any = null;
    try {
      if (typeof BarcodeDetector !== "undefined") {
        const muodot: string[] = await BarcodeDetector.getSupportedFormats?.() ?? [];
        if (muodot.includes("qr_code")) { detector = new BarcodeDetector({ formats: ["qr_code"] }); }
      }
      lukija = detector ? "barcodedetector" : "jsqr";
      if (lukija === "jsqr") await lataaJsQR();
    } catch (e) {
      setTila("virhe"); setViesti(t("ska.lukija_virhe", { syy: e instanceof Error ? e.message : String(e) })); return;
    }
    try {
      virta.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
    } catch (e) {
      const nimi = e instanceof Error ? e.name : "";
      if (nimi === "NotAllowedError" || nimi === "SecurityError") { setTila("ei_lupaa"); setViesti(t("ska.ei_lupaa")); }
      else if (nimi === "NotFoundError" || nimi === "OverconstrainedError") { setTila("ei_kameraa"); setViesti(t("ska.ei_kameraa")); }
      else { setTila("virhe"); setViesti(t("ska.kamera_virhe", { syy: e instanceof Error ? e.message : String(e) })); }
      return;
    }
    const v = video.current;
    if (!v) { sulje(); return; }
    v.srcObject = virta.current;
    try { await v.play(); } catch { /* autoplay-esto: käyttäjä painoi nappia, joten tämä ei yleensä tapahdu */ }
    setTila("lukee");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let viimeksi = 0;
    const askel = async (aika: number) => {
      if (!virta.current) return;
      // Noin 8 kuvaa sekunnissa riittää ja säästää puhelimen akkua.
      if (aika - viimeksi > 120 && v.readyState >= 2 && v.videoWidth > 0) {
        viimeksi = aika;
        let teksti: string | null = null;
        try {
          if (lukija === "barcodedetector") {
            const koodit = await detector.detect(v);
            teksti = koodit?.[0]?.rawValue ?? null;
          } else if (ctx && window.jsQR) {
            const w = Math.min(v.videoWidth, 640), h = Math.round(v.videoHeight * (w / v.videoWidth));
            canvas.width = w; canvas.height = h;
            ctx.drawImage(v, 0, 0, w, h);
            const kuva = ctx.getImageData(0, 0, w, h);
            teksti = window.jsQR(kuva.data, w, h)?.data ?? null;
          }
        } catch { teksti = null; }
        if (teksti) {
          const n = tarranNumero(teksti);
          if (n !== null) { sulje(); loytyi(n, teksti); return; }
          setViesti(t("ska.ei_tarra", { koodi: teksti.slice(0, 60) }));
        }
      }
      kehys.current = requestAnimationFrame(askel);
    };
    kehys.current = requestAnimationFrame(askel);
  };

  return (
    <div className="qr__skannaus" data-skannaus-tila={tila} data-skannaus-tuettu={tuettu && turvallinen ? "1" : "0"}>
      {tila !== "lukee" && tila !== "avataan" && (
        <button type="button" className="nappi" data-skannaus-nappi="" onClick={() => void avaa()}>{t("ska.skannaa")}</button>
      )}
      {(tila === "lukee" || tila === "avataan") && (
        <>
          <video ref={video} className="qr__video" playsInline muted autoPlay style={{ width: "100%", maxWidth: 420, background: "#000" }} />
          <p className="dim">{tila === "avataan" ? t("ska.avataan") : t("ska.osoita")}</p>
          <button type="button" className="nappi nappi--kevyt" onClick={sulje}>{t("ska.sulje")}</button>
        </>
      )}
      {viesti && <p className="qr__esto" role="status" data-skannaus-viesti="">{viesti}</p>}
    </div>
  );
}
